import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { FiMenu, FiX, FiChevronDown, FiChevronRight, FiChevronLeft, FiSmartphone, FiGlobe, FiFacebook, FiCode, FiLayers, FiTrendingUp, FiPenTool } from 'react-icons/fi';
import { MdDesignServices, MdDeveloperMode, MdSchool } from 'react-icons/md';
import { HiOutlineDesktopComputer } from 'react-icons/hi';
import "../../styles/Navbar.css"

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [mobileView, setMobileView] = useState('main');
  const [isMobile, setIsMobile] = useState(false);
  const dropdownRefs = useRef({});
  const submenuTimeoutRef = useRef(null);
  const navigate = useNavigate();

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close dropdown when clicking outside on desktop
  useEffect(() => {
    if (isMobile) return;

    const handleClickOutside = (event) => {
      let shouldClose = true;

      Object.values(dropdownRefs.current).forEach(ref => {
        if (ref && ref.contains(event.target)) {
          shouldClose = false;
        }
      });

      if (shouldClose) {
        setActiveDropdown(null);
        setActiveSubmenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    if (!isMobile || !isOpen) return;

    const handleClickOutside = (event) => {
      const menu = document.querySelector('.zoho-navbar-menu');
      const toggle = document.querySelector('.zoho-navbar-toggle');

      if (menu && toggle && !menu.contains(event.target) && !toggle.contains(event.target)) {
        handleCloseAll();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile, isOpen]);

  // Navbar.js - Complete platformsData array
  const platformsData = [
    {
      title: "Mobile Applications",
      icon: <FiSmartphone />,
      description: "Native and cross-platform mobile solutions",
      route: "/platforms/mobile-applications",
      submenu: [
        {
          title: "LookIt",
          route: "/platforms/mobile-applications#lookit",
          items: ["Information", "HealthTips", "Education", "TechTips"]
        },
        {
          title: "Makkal Sinthani Peravai",
          route: "/platforms/mobile-applications#makkal-sinthani-peravai",
          items: ["Barathi Vizha", "News", "Meetings", "Publications"]
        },
        {
          title: "FirstLook",
          route: "/platforms/mobile-applications#firstlook",
          items: ["Education", "Entertainment"]
        }
      ],
      color: "#6366f1"
    },
    {
      title: "Websites",
      icon: <FiGlobe />,
      description: "Responsive and dynamic web solutions",
      route: "/platforms/websites",
      submenu: [
        {
          title: "Readers Menu",
          route: "/platforms/websites#readers-menu",
          items: ["Information", "HealthTips", "Education", "TechTips"]
        },
        {
          title: "Today Talks",
          route: "/platforms/websites#today-talks",
          items: ["Sports", "Entertainment", "Education", "AIPrompts"]
        },
        {
          title: "Erode Book Festival",
          route: "/platforms/websites#erode-book-festival",
          items: ["Barathi Vizha", "News", "Meetings", "Publications"]
        },
        {
          title: "Makkal Sinthani Peravai",
          route: "/platforms/websites#makkal-sinthani-peravai-web",
          items: ["WordPress", "Custom CMS", "Headless CMS", "Content Management", "Blog Platforms"]
        },
        {
          title: "TStalin Gunasekaran",
          route: "/platforms/websites#tstalin-gunasekaran",
          items: ["Personal Information", "Portfolio", "Interviews", "Publications"]
        },
        {
          title: "Makkal Naatkaati",
          route: "/platforms/websites#makkal-naatkaati",
          items: ["Calendar", "Rasi", "Panchangam", "Natchathiram"]
        }
      ],
      color: "#10b981"
    },
    {
      title: "Social Media",
      icon: <FiFacebook />,
      description: "Social media platforms",
      route: "/platforms/social-media",
      submenu: [
        {
          title: "FaceBook",
          route: "/platforms/social-media#facebook",
          items: ["ReadersMenu", "TodayTalks", "FirstLook"]
        },
        {
          title: "Youtube",
          route: "/platforms/social-media#youtube",
          items: ["ReadersMenu", "MakkalNaatkaati"]
        },
        {
          title: "Instagram",
          route: "/platforms/social-media#instagram",
          items: ["ReadersMenu", "TodayTalks", "Mpeoples", "Mpeoples Attrocites", "FirstLook"]
        }
      ],
      color: "#3b82f6"
    }
  ];

  const servicesData = [
    {
      title: "Website & Application Development",
      icon: <FiCode />,
      description: "Custom digital solutions",
      route: "/services/website-development",
      submenu: [
        {
          id: "frontend-development",
          title: "Frontend Development",
          route: "/services/website-development#frontend-development",
          items: [
            "Business Websites",
            "Corporate Portals",
            "Landing Pages",
            "Portfolio Websites",
            "SEO-Friendly Websites"
          ]
        },
        {
          id: "backend-development",
          title: "Backend Development",
          route: "/services/website-development#backend-development",
          items: [
            "Admin Dashboards",
            "CRM Systems",
            "ERP Solutions",
            "API Development",
            "Custom Web Applications"
          ]
        },
        {
          id: "mobile-development",
          title: "Mobile Development",
          route: "/services/website-development#mobile-development",
          items: [
            "E-commerce Mobile Apps",
            "Business Apps",
            "Booking Apps",
            "Educational Apps",
            "On-Demand Service Apps"
          ]
        },
        {
          id: "e-commerce",
          title: "E-commerce",
          route: "/services/website-development#e-commerce",
          items: [
            "Online Stores",
            "Marketplace Platforms",
            "Subscription-Based Websites",
            "Digital Product Stores",
            "Payment & Order Management Systems"
          ]
        }
      ],
      color: "#8b5cf6"
    },
    {
      title: "Edu Tech",
      icon: <MdSchool />,
      description: "Educational technology solutions",
      route: "/services/edu-tech",
      submenu: [
        {
          id: "learning-platforms",
          title: "Learning Platforms",
          route: "/services/edu-tech#learning-platforms",
          items: [
            "Online Learning Platforms",
            "School & College LMS",
            "Virtual Classroom Systems",
            "Certification Platforms",
            "Coaching Institute Portals"
          ]
        },
        {
          id: "educational-tools",
          title: "Educational Tools",
          route: "/services/edu-tech#educational-tools",
          items: [
            "Online Exam Platforms",
            "Quiz & Practice Apps",
            "Student Progress Tracking Systems",
            "Parent Monitoring Portals",
            "Learning Analytics Platforms"
          ]
        },
        {
          id: "content-management",
          title: "Content Management",
          route: "/services/edu-tech#content-management",
          items: [
            "Course Management Systems",
            "Video Learning Platforms",
            "Digital Study Material Libraries",
            "Assignment Submission Portals",
            "Teacher Content Dashboards"
          ]
        },
        {
          id: "administration",
          title: "Administration",
          route: "/services/edu-tech#administration",
          items: [
            "School Management Systems",
            "College ERP Platforms",
            "Fee & Billing Systems",
            "Attendance & Timetable Portals",
            "Internal Communication Platforms"
          ]
        }
      ],
      color: "#0ea5e9"
    },
    {
      title: "HR Consultancy",
      icon: <FiLayers />,
      description: "Human resources solutions",
      route: "/services/hr-consultancy",
      submenu: [
        {
          id: "recruitment",
          title: "Recruitment",
          route: "/services/hr-consultancy#recruitment",
          items: [
            "Recruitment Management Systems",
            "Job Portal Platforms",
            "Hiring & Interview Platforms",
            "Candidate Tracking Systems",
            "Employee Onboarding Portals"
          ]
        },
        {
          id: "hr-management",
          title: "HR Management",
          route: "/services/hr-consultancy#hr-management",
          items: [
            "HR Management Systems",
            "Employee Self-Service Portals",
            "Leave & Attendance Platforms",
            "Payroll Management Systems",
            "Compliance Management Solutions"
          ]
        },
        {
          id: "training-development",
          title: "Training & Development",
          route: "/services/hr-consultancy#training-development",
          items: [
            "Employee Training Platforms",
            "Corporate Learning Systems",
            "Skill Development Portals",
            "Internal E-Learning Platforms",
            "Workshop & Certification Systems"
          ]
        },
        {
          id: "hr-analytics",
          title: "HR Analytics",
          route: "/services/hr-consultancy#hr-analytics",
          items: [
            "HR Analytics Dashboards",
            "Employee Performance Platforms",
            "Attrition Analysis Systems",
            "Salary & Compensation Dashboards",
            "HR Reporting Solutions"
          ]
        }
      ],
      color: "#f59e0b"
    },
    {
      title: "Digital Marketing",
      icon: <FiTrendingUp />,
      description: "Online marketing services",
      route: "/services/digital-marketing",
      submenu: [
        {
          id: "seo-services",
          title: "SEO Services",
          route: "/services/digital-marketing#seo-services",
          items: [
            "Business Website SEO",
            "Local Business SEO",
            "E-commerce SEO",
            "Corporate Website Optimization",
            "SEO Performance Reporting"
          ]
        },
        {
          id: "social-media",
          title: "Social Media",
          route: "/services/digital-marketing#social-media",
          items: [
            "Brand Social Media Pages",
            "Business Promotion Campaigns",
            "Lead Generation Campaigns",
            "Influencer Promotion Campaigns",
            "Social Media Growth Management"
          ]
        },
        {
          id: "content-marketing",
          title: "Content Marketing",
          route: "/services/digital-marketing#content-marketing",
          items: [
            "Business Blog Platforms",
            "Brand Video Marketing",
            "Educational Content Campaigns",
            "Email Marketing Systems",
            "Case Study & Portfolio Content"
          ]
        },
        {
          id: "ppc-advertising",
          title: "PPC & Advertising",
          route: "/services/digital-marketing#ppc-advertising",
          items: [
            "Google Ads Campaigns",
            "Social Media Ad Campaigns",
            "Corporate Lead Ads",
            "Product Promotion Ads",
            "Remarketing Campaigns"
          ]
        }
      ],
      color: "#10b981"
    },
    {
      title: "Graphic Designing & Logo Services",
      icon: <FiPenTool />,
      description: "Creative design solutions",
      route: "/services/graphic-designing",
      submenu: [
        {
          id: "logo-design",
          title: "Logo Design",
          route: "/services/graphic-designing#logo-design",
          items: [
            "Brand Logo Design",
            "Startup Branding Packages",
            "Corporate Identity Design",
            "Brand Guideline Documents",
            "Animated Logo Assets"
          ]
        },
        {
          id: "ui-ux-design",
          title: "UI/UX Design",
          route: "/services/graphic-designing#ui-ux-design",
          items: [
            "Website UI Design",
            "Mobile App UI Design",
            "Dashboard UI Design",
            "User Experience Optimization",
            "Design System Creation"
          ]
        },
        {
          id: "print-design",
          title: "Print Design",
          route: "/services/graphic-designing#print-design",
          items: [
            "Marketing Brochures",
            "Business Stationery",
            "Promotional Flyers",
            "Product Packaging Designs",
            "Outdoor Advertising Designs"
          ]
        },
        {
          id: "digital-graphics",
          title: "Digital Graphics",
          route: "/services/graphic-designing#digital-graphics",
          items: [
            "Social Media Creatives",
            "Website Visual Assets",
            "Presentation Designs",
            "Infographic Designs",
            "Icon & Illustration Sets"
          ]
        }
      ],
      color: "#ec4899"
    },
    {
      title: "Trademark & Copyright Services",
      icon: <MdDesignServices />,
      description: "Intellectual property protection",
      route: "/services/trademark-copyright",
      submenu: [
        {
          id: "trademark-services",
          title: "Trademark Services",
          route: "/services/trademark-copyright#trademark-services",
          items: [
            "Brand Trademark Registration",
            "Business Name Protection",
            "Logo Trademark Filing",
            "Trademark Renewal Services",
            "International Trademark Support"
          ]
        },
        {
          id: "copyright-services",
          title: "Copyright Services",
          route: "/services/trademark-copyright#copyright-services",
          items: [
            "Website Content Copyright",
            "Software & App Copyright",
            "Creative Work Protection",
            "Digital Rights Protection",
            "Copyright Licensing Support"
          ]
        },
        {
          id: "ip-strategy",
          title: "IP Strategy",
          route: "/services/trademark-copyright#ip-strategy",
          items: [
            "Startup IP Planning",
            "Brand Protection Strategy",
            "IP Risk Assessment",
            "Competitor IP Analysis",
            "IP Asset Valuation"
          ]
        },
        {
          id: "legal-support",
          title: "Legal Support",
          route: "/services/trademark-copyright#legal-support",
          items: [
            "Legal Documentation Services",
            "IP Agreement Drafting",
            "Compliance Verification",
            "Legal Notice Handling",
            "Expert IP Consultation"
          ]
        }
      ],
      color: "#6366f1"
    },
    {
      title: "Local Advertisement Services",
      icon: <HiOutlineDesktopComputer />,
      description: "Local business promotion",
      route: "/services/local-advertisement",
      submenu: [
        {
          id: "local-seo",
          title: "Local SEO",
          route: "/services/local-advertisement#local-seo",
          items: [
            "Local Business SEO",
            "Google Maps Ranking Services",
            "City-Based Business Promotion",
            "Local Search Visibility Boost",
            "Review & Reputation Management"
          ]
        },
        {
          id: "local-advertising",
          title: "Local Advertising",
          route: "/services/local-advertisement#local-advertising",
          items: [
            "Local Paid Ad Campaigns",
            "Neighborhood Business Promotion",
            "Event-Based Advertising",
            "Area-Targeted Marketing",
            "Local Brand Awareness Campaigns"
          ]
        },
        {
          id: "directory-listings",
          title: "Directory Listings",
          route: "/services/local-advertisement#directory-listings",
          items: [
            "Business Directory Listings",
            "Industry-Specific Listings",
            "Local Review Platforms",
            "City Portals & Media Listings",
            "Community Business Boards"
          ]
        },
        {
          id: "local-presence",
          title: "Local Presence",
          route: "/services/local-advertisement#local-presence",
          items: [
            "Local Content Marketing",
            "Community Engagement Campaigns",
            "Local PR & News Promotion",
            "Influencer-Based Local Marketing",
            "Hyperlocal Branding Strategies"
          ]
        }
      ],
      color: "#f97316"
    }
  ];

  // Training data with submenus and routes
  const trainingData = [
    {
      title: "UI/UX Design Training",
      icon: <MdDesignServices />,
      description: "Master user interface and experience design",
      route: "/training/ui-ux-design",
      submenu: [
        {
          id: "fundamentals",
          title: "Fundamentals",
          route: "/training/ui-ux-design#fundamentals",
          items: ["Design Principles", "Color Theory", "Typography", "Layout Design", "Design Systems"]
        },
        {
          id: "tools",
          title: "Tools",
          route: "/training/ui-ux-design#tools",
          items: ["Figma Mastery", "Adobe XD", "Sketch", "Prototyping Tools", "Collaboration Platforms"]
        },
        {
          id: "research",
          title: "Research",
          route: "/training/ui-ux-design#research",
          items: ["User Research", "Persona Creation", "User Testing", "Analytics", "Competitor Analysis"]
        },
        {
          id: "portfolio",
          title: "Portfolio",
          route: "/training/ui-ux-design#portfolio",
          items: ["Case Studies", "Design Projects", "Client Work", "Presentation Skills", "Interview Prep"]
        }
      ],
      color: "#8b5cf6",
      duration: "6 Weeks",
      internCount: 3,
      internLabel: "3 UI/UX Design Interns"
    },
    {
      title: "Full Stack Development",
      icon: <MdDeveloperMode />,
      description: "Complete web development training",
      route: "/training/full-stack-development",
      submenu: [
        {
          id: "frontend",
          title: "Frontend",
          route: "/training/full-stack-development#frontend",
          items: ["React.js", "JavaScript ES6+", "CSS Frameworks", "State Management", "Web Performance"]
        },
        {
          id: "backend",
          title: "Backend",
          route: "/training/full-stack-development#backend",
          items: ["Node.js/Express", "Database Design", "API Development", "Authentication", "Server Management"]
        },
        {
          id: "devops",
          title: "DevOps",
          route: "/training/full-stack-development#devops",
          items: ["Git & GitHub", "CI/CD Pipeline", "Docker Basics", "Deployment", "Testing Strategies"]
        },
        {
          id: "projects",
          title: "Projects",
          route: "/training/full-stack-development#projects",
          items: ["Real Projects", "Code Reviews", "Best Practices", "Team Collaboration", "Agile Methodology"]
        }
      ],
      color: "#10b981",
      duration: "12 Weeks",
      internCount: 35,
      internLabel: "35 Full Stack Interns"
    },
    {
      title: "Frontend Development",
      icon: <FiCode />,
      description: "Modern frontend development skills",
      route: "/training/frontend-development",
      submenu: [
        {
          id: "core-technologies",
          title: "Core Technologies",
          route: "/training/frontend-development#core-technologies",
          items: ["HTML5", "CSS3/SASS", "JavaScript", "TypeScript", "Web APIs"]
        },
        {
          id: "frameworks",
          title: "Frameworks",
          route: "/training/frontend-development#frameworks",
          items: ["React.js", "Vue.js", "Angular", "Next.js", "State Management"]
        },
        {
          id: "tools-practices",
          title: "Tools & Practices",
          route: "/training/frontend-development#tools-practices",
          items: ["Webpack", "NPM/Yarn", "Testing", "Debugging", "Performance"]
        },
        {
          id: "advanced-topics",
          title: "Advanced Topics",
          route: "/training/frontend-development#advanced-topics",
          items: ["PWA", "Web Components", "Animation", "Accessibility", "SEO Basics"]
        }
      ],
      color: "#3b82f6",
      duration: "8 Weeks",
      internCount: 8,
      internLabel: "8 Mini Project Interns"
    },
    {
      title: "Backend Development",
      icon: <FiCode />,
      description: "Server-side development training",
      route: "/training/backend-development",
      submenu: [
        {
          id: "server-technologies",
          title: "Server Technologies",
          route: "/training/backend-development#server-technologies",
          items: ["Node.js", "Python", "Java", "PHP", ".NET"]
        },
        {
          id: "databases",
          title: "Databases",
          route: "/training/backend-development#databases",
          items: ["MongoDB", "MySQL", "PostgreSQL", "Redis", "ORM/ODM"]
        },
        {
          id: "api-development",
          title: "API Development",
          route: "/training/backend-development#api-development",
          items: ["REST APIs", "GraphQL", "WebSockets", "Microservices", "API Documentation"]
        },
        {
          id: "security-deployment",
          title: "Security & Deployment",
          route: "/training/backend-development#security-deployment",
          items: ["Authentication", "Authorization", "Security Best Practices", "Deployment", "Monitoring"]
        }
      ],
      color: "#f59e0b",
      duration: "10 Weeks",
      internCount: 2,
      internLabel: "2 Human Resources Interns"
    },
    {
      title: "Digital Marketing",
      icon: <FiTrendingUp />,
      description: "Comprehensive digital marketing training",
      route: "/training/digital-marketing",
      submenu: [
        {
          id: "seo",
          title: "SEO",
          route: "/training/digital-marketing#seo",
          items: ["Keyword Research", "On-page SEO", "Technical SEO", "Local SEO", "SEO Tools"]
        },
        {
          id: "social-media",
          title: "Social Media",
          route: "/training/digital-marketing#social-media",
          items: ["Content Strategy", "Platform Management", "Advertising", "Analytics", "Community Building"]
        },
        {
          id: "content-marketing",
          title: "Content Marketing",
          route: "/training/digital-marketing#content-marketing",
          items: ["Blog Writing", "Video Marketing", "Email Marketing", "Content Strategy", "Analytics"]
        },
        {
          id: "paid-advertising",
          title: "Paid Advertising",
          route: "/training/digital-marketing#paid-advertising",
          items: ["Google Ads", "Facebook Ads", "LinkedIn Ads", "Analytics", "Conversion Optimization"]
        }
      ],
      color: "#ec4899",
      duration: "6 Weeks",
      internCount: 1,
      internLabel: "1 Digital Marketing Intern"
    },
    {
      title: "Graphic Designing",
      icon: <FiPenTool />,
      description: "Professional graphic design training",
      route: "/training/graphic-designing",
      submenu: [
        {
          id: "design-fundamentals",
          title: "Design Fundamentals",
          route: "/training/graphic-designing#design-fundamentals",
          items: ["Design Principles", "Color Theory", "Typography", "Layout", "Composition"]
        },
        {
          id: "software-skills",
          title: "Software Skills",
          route: "/training/graphic-designing#software-skills",
          items: ["Adobe Photoshop", "Illustrator", "InDesign", "Figma", "Canva Pro"]
        },
        {
          id: "specializations",
          title: "Specializations",
          route: "/training/graphic-designing#specializations",
          items: ["Logo Design", "Brand Identity", "Print Design", "UI Design", "Illustration"]
        },
        {
          id: "business-skills",
          title: "Business Skills",
          route: "/training/graphic-designing#business-skills",
          items: ["Client Management", "Portfolio Building", "Pricing Strategies", "Freelancing", "Copyright"]
        }
      ],
      color: "#6366f1",
      duration: "8 Weeks",
      internCount: 1,
      internLabel: "1 Graphic Designing Intern"
    }
  ];

  const handleDropdownEnter = (dropdownName) => {
    if (submenuTimeoutRef.current) {
      clearTimeout(submenuTimeoutRef.current);
    }

    if (!isMobile) {
      setActiveDropdown(dropdownName);
      setActiveSubmenu(0);
    }
  };

  const handleDropdownLeave = () => {
    submenuTimeoutRef.current = setTimeout(() => {
      if (!isMobile) {
        setActiveDropdown(null);
        setActiveSubmenu(null);
      }
    }, 300);
  };

  const handleDropdownClick = (dropdownName) => {
    if (isMobile) {
      setActiveDropdown(dropdownName);
      setMobileView('menu');
      setActiveSubmenu(0);
    } else {
      setActiveDropdown(dropdownName);
      setActiveSubmenu(0);
    }
  };

  const handleMenuItemHover = (index) => {
    if (!isMobile) {
      setActiveSubmenu(index);
    }
  };

  const handleMenuItemClick = (index) => {
    if (isMobile) {
      setActiveSubmenu(index);
      setMobileView('submenu');
    } else {
      setActiveSubmenu(index);
    }
  };

  const handleMobileMenuBack = () => {
    if (mobileView === 'submenu') {
      setMobileView('menu');
    } else if (mobileView === 'menu') {
      setMobileView('main');
      setActiveDropdown(null);
      setActiveSubmenu(null);
    }
  };

  const handleMobileNavClose = () => {
    setIsOpen(false);
    setMobileView('main');
    setActiveDropdown(null);
    setActiveSubmenu(null);
  };

  const handleCloseAll = () => {
    setIsOpen(false);
    setMobileView('main');
    setActiveDropdown(null);
    setActiveSubmenu(null);
  };

  // Navbar.js - Add these navigation handlers at the top of the component
  const handleCategoryClick = (route) => {
    console.log('Category click - navigating to:', route);
    navigate(route);
    handleCloseAll();
  };

  const handleSubCategoryClick = (route) => {
    console.log('Subcategory click - navigating to:', route);
    // Split the route to handle hash navigation
    const [path, hash] = route.split('#');
    navigate(path);

    // Scroll to the section after a small delay
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }

    handleCloseAll();
  };

  const handleSubmenuItemClick = (section, index) => {
    console.log('Submenu item click:', section.title);
    handleSubCategoryClick(section.route);
  };

  const getDropdownData = (dropdownName) => {
    switch (dropdownName) {
      case 'platforms': return platformsData;
      case 'services': return servicesData;
      case 'training': return trainingData;
      default: return [];
    }
  };

  const getDropdownTitle = (dropdownName) => {
    switch (dropdownName) {
      case 'platforms': return "Our Platforms";
      case 'services': return "Our Services";
      case 'training': return "Training Programs";
      default: return "";
    }
  };

  // Render mobile menu view
  const renderMobileMenu = () => {
    const data = getDropdownData(activeDropdown);
    const title = getDropdownTitle(activeDropdown);

    if (mobileView === 'menu') {
      return (
        <div className="mobile-menu-slide">
          <div className="mobile-menu-header">
            <button className="mobile-menu-back" onClick={handleMobileMenuBack}>
              <FiChevronLeft size={20} />
              <span>Back</span>
            </button>
            <h3 className="mobile-menu-title">{title}</h3>
            <div className="mobile-menu-close" onClick={handleMobileNavClose}>
              <FiX size={20} />
            </div>
          </div>

          <div className="mobile-menu-items">
            {data.map((item, index) => (
              <div
                key={index}
                className="mobile-menu-item"
                onClick={() => handleMenuItemClick(index)}
              >
                <div className="mobile-item-header">
                  <div className="mobile-item-content">
                    <h4>{item.title}</h4>
                  </div>
                  <FiChevronRight className="mobile-item-arrow" />
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (mobileView === 'submenu' && activeSubmenu !== null) {
      const item = data[activeSubmenu];

      return (
        <div className="mobile-submenu-slide">
          <div className="mobile-submenu-header">
            <button className="mobile-submenu-back" onClick={handleMobileMenuBack}>
              <FiChevronLeft size={20} />
              <span>Back</span>
            </button>
            <h3 className="mobile-submenu-title">{item.title}</h3>
            <div className="mobile-submenu-close" onClick={handleMobileNavClose}>
              <FiX size={20} />
            </div>
          </div>

          <div className="mobile-submenu-content">
            <div className="mobile-submenu-info">
              {/* View All Button */}
              <button
                className="mobile-view-all-btn"
                onClick={() => handleCategoryClick(item.route)}
              >
                View All {item.title}
              </button>
            </div>

            <div className="mobile-submenu-sections">
              {item.submenu.map((section, sectionIndex) => (
                <div key={sectionIndex} className="mobile-submenu-section">
                  <div className="mobile-section-header">
                    <h4 className="mobile-section-title">{section.title}</h4>
                    <button
                      className="mobile-section-link"
                      onClick={() => handleSubCategoryClick(section.route)}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  // Render desktop dropdown
  const renderDropdown = (dropdownName, title) => {
    const data = getDropdownData(dropdownName);

    return (
      <div className="zoho-mega-dropdown">
        <div className="mega-dropdown-container">
          {/* Main Menu Items */}
          <div className="mega-menu-main">
            <div className="mega-menu-items">
              {data.map((item, index) => (
                <div
                  key={index}
                  className={`mega-menu-item ${activeSubmenu === index ? 'active' : ''}`}
                  onMouseEnter={() => handleMenuItemHover(index)}
                  onClick={() => {
                    handleMenuItemClick(index);
                    handleCategoryClick(item.route);
                  }}
                >
                  <div className="mega-item-header">
                    <div className="mega-item-icon" style={{ backgroundColor: `${item.color}20`, color: item.color }}>
                      {item.icon}
                    </div>
                    <div className="mega-item-content">
                      <h4>{item.title}</h4>
                      {/* <p className="mega-item-desc">{item.description}</p>
                      {item.duration && (
                        <span className="duration-badge">{item.duration}</span>
                      )} */}
                    </div>
                    <FiChevronRight className="mega-item-arrow" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Submenu Section */}
          <div className="mega-submenu">
            {activeSubmenu !== null && data[activeSubmenu]?.submenu ? (
              <div className="submenu-content">
                <div className="submenu-grid">
                  {data[activeSubmenu].submenu.map((section, sectionIndex) => (
                    <div key={sectionIndex} className="submenu-section">
                      <div className="section-header7">
                        <h4 className="section-title1">{section.title}</h4>
                        <button
                          className="section-link"
                          onClick={() => handleSubCategoryClick(section.route)}
                        >
                          <img className='section-linkimg' src="/assets/arrow.png" alt="" />
                        </button>
                      </div>
                      <ul className="section-items9">
                        {section.items.map((item, itemIndex) => (
                          <li key={itemIndex} onClick={() => handleSubmenuItemClick(section, itemIndex)}>
                            <div className="item-bullet" style={{ backgroundColor: data[activeSubmenu].color }}></div>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="submenu-placeholder">
                <div className="placeholder-icon">👉</div>
                <p>Select a menu item to view details</p>
                <p className="placeholder-hint">Hover over menu items to see submenus</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <header className="zoho-navbar">
      <div className="zoho-navbar-container">
        {/* LOGO */}
        <Link to="/" className="zoho-navbar-logo" onClick={handleCloseAll}>
          <img className='logoimg' src="/assets/mplogo.png" alt="Company Logo" />
          <div className="zoho-brand-text">
            <span className="zoho-brand-name">Mpeoples</span>
            <span className="zoho-brand-sub">Business Solutions Pvt Ltd</span>
          </div>
        </Link>

        {/* MENU TOGGLE (MOBILE) */}
        <button
          className="zoho-navbar-toggle"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle navigation"
        >
          {isOpen ? <FiX size={22} /> : <FiMenu size={22} />}
        </button>

        {/* NAV LINKS - Desktop */}
        <nav className={`zoho-navbar-menu ${isOpen ? 'open' : ''}`}>
          {/* Mobile Backdrop */}
          {isMobile && isOpen && (
            <div className="mobile-menu-backdrop" onClick={handleMobileNavClose}></div>
          )}

          {/* Mobile Navigation Slides */}
          <div className={`mobile-nav-content ${mobileView !== 'main' ? 'hidden' : ''}`}>
            <ul className="zoho-navbar-links">
              <li>
                <NavLink to="/" onClick={handleMobileNavClose}>
                  Home
                </NavLink>
              </li>

              {/* PLATFORMS DROPDOWN */}
              <li
                className="zoho-navbar-dropdown-parent"
                ref={el => dropdownRefs.current.platforms = el}
                onMouseEnter={() => handleDropdownEnter('platforms')}
                onMouseLeave={handleDropdownLeave}
              >
                <div
                  className="zoho-dropdown-trigger"
                  onClick={() => handleDropdownClick('platforms')}
                >
                  <span>Our Platforms</span>
                  <FiChevronDown className={`dropdown-icon ${activeDropdown === 'platforms' ? 'rotated' : ''}`} />
                </div>

                {!isMobile && activeDropdown === 'platforms' && renderDropdown('platforms', 'Our Platforms')}
              </li>

              {/* CLIENT PROJECTS DROPDOWN */}
              <li>
                <NavLink to="/clientprojects" onClick={handleMobileNavClose}>
                  Client Projects
                </NavLink>
              </li>

              {/* SERVICES DROPDOWN */}
              <li
                className="zoho-navbar-dropdown-parent"
                ref={el => dropdownRefs.current.services = el}
                onMouseEnter={() => handleDropdownEnter('services')}
                onMouseLeave={handleDropdownLeave}
              >
                <div
                  className="zoho-dropdown-trigger"
                  onClick={() => handleDropdownClick('services')}
                >
                  <span>Services</span>
                  <FiChevronDown className={`dropdown-icon ${activeDropdown === 'services' ? 'rotated' : ''}`} />
                </div>

                {!isMobile && activeDropdown === 'services' && renderDropdown('services', 'Our Services')}
              </li>

              {/* TRAINING DROPDOWN */}
              <li
                className="zoho-navbar-dropdown-parent"
                ref={el => dropdownRefs.current.training = el}
                onMouseEnter={() => handleDropdownEnter('training')}
                onMouseLeave={handleDropdownLeave}
              >
                <div
                  className="zoho-dropdown-trigger"
                  onClick={() => handleDropdownClick('training')}
                >
                  <span>Training</span>
                  <FiChevronDown className={`dropdown-icon ${activeDropdown === 'training' ? 'rotated' : ''}`} />
                </div>

                {!isMobile && activeDropdown === 'training' && renderDropdown('training', 'Training Programs')}
              </li>

              <li>
                <NavLink to="/contact" onClick={handleMobileNavClose}>
                  Contact
                </NavLink>
              </li>
            </ul>

            {/* CTA BUTTON */}
            <div className="zoho-navbar-cta">
              <Link
                to="/contact"
                className="zoho-btn zoho-btn-primary"
                onClick={handleMobileNavClose}
              >
                Get Free Quote
              </Link>
            </div>
          </div>

          {/* Mobile Menu Slide */}
          {isMobile && isOpen && mobileView !== 'main' && renderMobileMenu()}
        </nav>
      </div>
    </header>
  );
};

export default Navbar;